import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { money } from "@/mechanic/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Layers, Plus, Pencil, Trash2, X } from "lucide-react";

const blank = {
  name: "", category: "",
  default_complaint: "",
  labor_lines: [],
  parts_lines: [],
  oil_lines: [],
  shop_supply_fee: "",
  is_oil_change: false,
  notes: "",
};

export default function CannedJobs() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState(null);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["mech-canned"],
    queryFn: async () => (await api.get("/canned-jobs")).data,
  });

  const save = useMutation({
    mutationFn: (body) => editing ? api.put(`/canned-jobs/${editing}`, body) : api.post("/canned-jobs", body),
    onSuccess: () => {
      toast.success("Saved");
      qc.invalidateQueries({ queryKey: ["mech-canned"] });
      setOpen(false); setEditing(null); setForm(blank);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/canned-jobs/${id}`),
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["mech-canned"] }); },
  });

  const openNew = () => { setForm(blank); setEditing(null); setOpen(true); };
  const openEdit = (c) => {
    setForm({
      name: c.name, category: c.category || "",
      default_complaint: c.default_complaint || "",
      labor_lines: (c.labor_lines || []).map((l) => ({ ...l })),
      parts_lines: (c.parts_lines || []).map((l) => ({ ...l })),
      oil_lines: (c.oil_lines || []).map((l) => ({ ...l })),
      shop_supply_fee: c.shop_supply_fee || "",
      is_oil_change: !!c.is_oil_change, notes: c.notes || "",
    });
    setEditing(c.id); setOpen(true);
  };

  const submit = () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    save.mutate({
      ...form,
      shop_supply_fee: Number(form.shop_supply_fee) || 0,
      labor_lines: form.labor_lines.map((l) => ({
        description: l.description || "",
        hours: Number(l.hours) || 0,
        rate_cost: Number(l.rate_cost) || 0,
        rate_charge: Number(l.rate_charge) || Number(l.rate_cost) || 0,
        tech_id: "", tech_name: "",
      })),
      parts_lines: form.parts_lines.map((p) => ({
        part_id: "", name: p.name || "",
        part_number: p.part_number || "",
        qty: Number(p.qty) || 1,
        unit_cost: Number(p.unit_cost) || 0,
        unit_price: Number(p.unit_price) || Number(p.unit_cost) || 0,
        supplier: "",
      })),
      oil_lines: form.oil_lines.map((o) => ({
        oil_id: "", oil_name: o.oil_name || "",
        litres: Number(o.litres) || 0,
        cost_per_litre: Number(o.cost_per_litre) || 0,
      })),
    });
  };

  const priceEst = (c) => {
    const l = (c.labor_lines || []).reduce((s, x) => s + Number(x.hours || 0) * Number(x.rate_charge || x.rate_cost || 0), 0);
    const p = (c.parts_lines || []).reduce((s, x) => s + Number(x.qty || 0) * Number(x.unit_price || x.unit_cost || 0), 0);
    return l + p + Number(c.shop_supply_fee || 0);
  };

  return (
    <div className="p-8" data-testid="canned-jobs-page">
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <Layers className="h-6 w-6 text-primary" />
          <div>
            <h1 className="font-heading text-3xl">Canned Jobs</h1>
            <p className="text-muted-foreground text-sm">Reusable service templates — build once, add to any work order in one click.</p>
          </div>
        </div>
        <Button onClick={openNew} data-testid="new-canned-btn"><Plus className="h-4 w-4 mr-1" />New canned job</Button>
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/30">
            <tr>
              <th className="text-left px-4 py-3 label-mono font-normal">Name</th>
              <th className="text-left px-4 py-3 label-mono font-normal">Category</th>
              <th className="text-right px-4 py-3 label-mono font-normal">Est. price</th>
              <th className="text-right px-4 py-3 label-mono font-normal">Used</th>
              <th className="text-right px-4 py-3 label-mono font-normal w-40">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={5} className="text-center text-muted-foreground p-6">Loading…</td></tr>}
            {!isLoading && rows.length === 0 && <tr><td colSpan={5} className="text-center text-muted-foreground p-6">No canned jobs yet — click &quot;New canned job&quot; to build your first template.</td></tr>}
            {rows.map((c) => (
              <tr key={c.id} className="border-t border-border hover:bg-secondary/20" data-testid={`canned-row-${c.id}`}>
                <td className="px-4 py-3 font-medium">
                  {c.name}
                  {c.is_oil_change && <Badge className="ml-2 text-[10px] bg-amber-500 text-white">Oil</Badge>}
                  {c.default_complaint && <div className="text-xs text-muted-foreground">{c.default_complaint}</div>}
                </td>
                <td className="px-4 py-3">{c.category || "—"}</td>
                <td className="px-4 py-3 text-right font-mono-data">{money(priceEst(c))}</td>
                <td className="px-4 py-3 text-right">{c.use_count || 0}×</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-canned-${c.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => { if (confirm(`Delete "${c.name}"?`)) del.mutate(c.id); }} data-testid={`del-canned-${c.id}`}>
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(blank); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="canned-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit canned job" : "New canned job"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <Label className="text-sm">Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Oil change — 5W-30 synthetic" data-testid="canned-name" />
              </div>
              <div>
                <Label className="text-sm">Category</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Oil, Brakes, Tires…" data-testid="canned-category" />
              </div>
            </div>
            <div className="col-span-2">
              <Label className="text-sm">Default complaint / description</Label>
              <Input value={form.default_complaint} onChange={(e) => setForm({ ...form, default_complaint: e.target.value })} placeholder="Auto-fills the WO complaint field" />
            </div>

            {/* LABOR */}
            <SectionEditor
              title="Labor lines"
              rows={form.labor_lines}
              onChange={(labor_lines) => setForm({ ...form, labor_lines })}
              blank={{ description: "", hours: 1, rate_cost: 0, rate_charge: 0 }}
              testidPrefix="canned-labor"
              renderRow={(l, upd) => (
                <>
                  <Input className="col-span-4" placeholder="Description" value={l.description} onChange={(e) => upd({ description: e.target.value })} />
                  <Input className="col-span-2" type="number" placeholder="Hrs" value={l.hours} onChange={(e) => upd({ hours: e.target.value })} />
                  <Input className="col-span-2" type="number" placeholder="Pay $/h" value={l.rate_cost} onChange={(e) => upd({ rate_cost: e.target.value })} />
                  <Input className="col-span-2" type="number" placeholder="Bill $/h" value={l.rate_charge} onChange={(e) => upd({ rate_charge: e.target.value })} />
                </>
              )}
            />

            {/* PARTS */}
            <SectionEditor
              title="Parts / materials"
              rows={form.parts_lines}
              onChange={(parts_lines) => setForm({ ...form, parts_lines })}
              blank={{ name: "", part_number: "", qty: 1, unit_cost: 0, unit_price: 0 }}
              testidPrefix="canned-part"
              renderRow={(p, upd) => (
                <>
                  <Input className="col-span-4" placeholder="Part name" value={p.name} onChange={(e) => upd({ name: e.target.value })} />
                  <Input className="col-span-2" placeholder="Part #" value={p.part_number} onChange={(e) => upd({ part_number: e.target.value })} />
                  <Input className="col-span-1" type="number" placeholder="Qty" value={p.qty} onChange={(e) => upd({ qty: e.target.value })} />
                  <Input className="col-span-2" type="number" placeholder="Cost" value={p.unit_cost} onChange={(e) => upd({ unit_cost: e.target.value })} />
                  <Input className="col-span-1" type="number" placeholder="Sell" value={p.unit_price} onChange={(e) => upd({ unit_price: e.target.value })} />
                </>
              )}
            />

            <div className="col-span-2 grid grid-cols-3 gap-4 border-t border-border pt-3">
              <div>
                <Label className="text-sm">Shop supplies fee</Label>
                <Input type="number" value={form.shop_supply_fee} onChange={(e) => setForm({ ...form, shop_supply_fee: e.target.value })} placeholder="0.00" />
              </div>
              <label className="flex items-center gap-2 self-end pb-2 cursor-pointer">
                <Checkbox checked={form.is_oil_change} onCheckedChange={(v) => setForm({ ...form, is_oil_change: !!v })} data-testid="canned-oil" />
                <span className="text-sm">Counts as an oil change</span>
              </label>
              <div className="col-span-3">
                <Label className="text-sm">Notes (internal)</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={save.isPending || !form.name.trim()} data-testid="canned-save">
              {save.isPending ? "Saving…" : (editing ? "Update" : "Create canned job")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SectionEditor({ title, rows, onChange, blank, renderRow, testidPrefix }) {
  const add = () => onChange([...rows, { ...blank }]);
  const rm = (i) => onChange(rows.filter((_, idx) => idx !== i));
  const upd = (i, patch) => onChange(rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  return (
    <div className="col-span-2 border-t border-border pt-3">
      <div className="flex items-center justify-between mb-2">
        <Label className="font-semibold">{title}</Label>
        <Button type="button" size="sm" variant="outline" onClick={add} data-testid={`${testidPrefix}-add`}><Plus className="h-3 w-3 mr-1" />Add</Button>
      </div>
      {rows.length === 0 && <p className="text-xs text-muted-foreground py-2">None yet.</p>}
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`${testidPrefix}-row-${i}`}>
            {renderRow(r, (patch) => upd(i, patch))}
            <Button type="button" variant="ghost" size="icon" className="col-span-1" onClick={() => rm(i)}><X className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}
